import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  signUp: (email: string, password: string) => Promise<{ error: Error | null; needsEmailVerification?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Function to manage session IDs
const manageSessionId = (action: 'create' | 'destroy') => {
  if (action === 'create') {
    const newSessionId = uuidv4();
    sessionStorage.setItem('footyAI_sessionId', newSessionId);
    return newSessionId;
  } else {
    sessionStorage.removeItem('footyAI_sessionId');
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Handle session ID management
      if (event === 'SIGNED_IN') {
        manageSessionId('create');
      } else if (event === 'SIGNED_OUT') {
        manageSessionId('destroy');
        // Clear all React Query cache on sign out
        queryClient.clear();
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const signUp = async (email: string, password: string) => {
    try {
      const referralCode = localStorage.getItem('referral_code');
      
      // First try to sign in to check if user exists
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      // If sign in succeeds, user exists and is now logged in
      if (signInData.session) {
        manageSessionId('create');
        return { error: null, needsEmailVerification: false };
      }

      // If sign in fails with wrong password, user exists but password is wrong
      if (signInError && signInError.message.includes('Invalid login credentials')) {
        return { error: new Error('An account with this email already exists') };
      }

      // If we get here, user doesn't exist, so create new account
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: referralCode ? {
            referral_code: referralCode
          } : undefined
        },
      });
      
      if (error) {
        return { error: new Error(error.message) };
      }

      // Clear referral code after successful signup
      if (referralCode) {
        localStorage.removeItem('referral_code');
      }

      // New user needs email verification
      return { error: null, needsEmailVerification: true };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('An error occurred during sign up') };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        return { error: new Error(error.message) };
      }

      // Create new session ID on successful sign in
      manageSessionId('create');
      
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('An error occurred during sign in') };
    }
  };

  const signInWithMagicLink = async (email: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        return { error: new Error(error.message) };
      }
      
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('An error occurred sending magic link') };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        return { error: new Error(error.message) };
      }
      
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('An error occurred during password reset') };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        return { error: new Error(error.message) };
      }
      
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('An error occurred updating password') };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Destroy session ID and clear state
      manageSessionId('destroy');
      setSession(null);
      setUser(null);

      // Clear all React Query cache
      queryClient.clear();

      // Navigate to home page
      navigate('/', { replace: true });

      // Refresh the page after a short delay to ensure state is cleared
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      session, 
      user, 
      signUp, 
      signIn,
      signInWithMagicLink,
      resetPassword,
      updatePassword,
      signOut, 
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};